/* SearchBar - Navbar search with custom clear icon and grouped results dropdown.
   Updated: Fix missing product thumbnails by filtering product-level images and adding fallback.
   Updated: Added DataFast search_used goal when a result is clicked. */

import { useEffect, useRef, useState } from 'react';
import { Search, X, Package, FileText, BookOpen, ArrowRight, Loader as Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getProductImageUrl } from '@/lib/image-url';
import { trackGoal } from '@/lib/datafast';

interface ProductResult {
  id: string;
  name: string;
  base_slug: string;
  is_simple_product: boolean;
  thumbnail: string;
  href: string;
}

interface BlogResult {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
}

interface PageResult {
  slug: string;
  title: string;
  description: string;
}

interface SearchResults {
  products: ProductResult[];
  blogs: BlogResult[];
  pages: PageResult[];
}

interface SearchBarProps {
  variant: 'desktop' | 'mobile';
}

export default function SearchBar({ variant }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>({ products: [], blogs: [], pages: [] });
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasQuery = query.trim().length >= 2;
  const hasResults = results.products.length > 0 || results.blogs.length > 0 || results.pages.length > 0;

  useEffect(() => {
    if (!hasQuery) {
      setShowDropdown(false);
      setResults({ products: [], blogs: [], pages: [] });
      setActiveIndex(-1);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setShowDropdown(true);
      try {
        const trimmed = query.trim();

        const [productsRes, blogsRes, pagesRes] = await Promise.all([
          supabase
            .from('products')
            .select('id, name, base_slug, is_simple_product, category_id')
            .eq('active', true)
            .or(`name.ilike.%${trimmed}%,description.ilike.%${trimmed}%`)
            .order('name', { ascending: true })
            .limit(5),
          supabase
            .from('blogs')
            .select('id, title, slug, excerpt')
            .eq('published', true)
            .or(`title.ilike.%${trimmed}%,excerpt.ilike.%${trimmed}%`)
            .order('published_at', { ascending: false })
            .limit(4),
          supabase
            .from('klantenservice_pages')
            .select('slug, title, description')
            .eq('active', true)
            .or(`title.ilike.%${trimmed}%,description.ilike.%${trimmed}%`)
            .order('display_order', { ascending: true })
            .limit(4),
        ]);

        const products: ProductResult[] = await Promise.all(
          (productsRes.data || []).map(async (p: any) => {
            let href = `/${p.base_slug}`;
            if (!p.is_simple_product) {
              const { data: variants } = await supabase
                .from('product_variants')
                .select('slug')
                .eq('product_id', p.id)
                .eq('active', true)
                .order('display_order', { ascending: true })
                .limit(1);
              if (variants && variants.length === 1) href = `/${variants[0].slug}`;
              else href = `/${p.base_slug}`;
            }

            const { data: image } = await supabase
              .from('product_images')
              .select('storage_path, uses_new_bucket')
              .eq('product_id', p.id)
              .is('variant_id', null)
              .eq('is_primary', true)
              .maybeSingle();

            let thumbnail = image?.storage_path ? getProductImageUrl(image.storage_path, image.uses_new_bucket, 'thumb') : '';

            if (!thumbnail) {
              const { data: fallback } = await supabase
                .from('product_images')
                .select('storage_path, uses_new_bucket')
                .eq('product_id', p.id)
                .order('display_order', { ascending: true })
                .limit(1)
                .maybeSingle();
              thumbnail = fallback?.storage_path ? getProductImageUrl(fallback.storage_path, fallback.uses_new_bucket, 'thumb') : '';
            }

            return {
              id: p.id,
              name: p.name,
              base_slug: p.base_slug,
              is_simple_product: p.is_simple_product,
              thumbnail,
              href,
            };
          })
        );

        setResults({
          products,
          blogs: blogsRes.data || [],
          pages: pagesRes.data || [],
        });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, hasQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allItems = [
    ...results.products.map((p) => ({ type: 'product' as const, ...p })),
    ...results.blogs.map((b) => ({ type: 'blog' as const, ...b })),
    ...results.pages.map((p) => ({ type: 'page' as const, ...p })),
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || allItems.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % allItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? allItems.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const item = allItems[activeIndex];
      if (item.type === 'product') window.location.href = (item as ProductResult).href;
      else if (item.type === 'blog') window.location.href = `/blogs/${(item as BlogResult).slug}`;
      else if (item.type === 'page') window.location.href = `/klantenservice/${(item as PageResult).slug}`;
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      inputRef.current?.blur();
    }
  };

  const handleClear = () => {
    setQuery('');
    setShowDropdown(false);
    setResults({ products: [], blogs: [], pages: [] });
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleResultClick = () => {
    trackGoal('search_used', { search_term: query.trim() });
    setShowDropdown(false);
    setQuery('');
    setResults({ products: [], blogs: [], pages: [] });
    setActiveIndex(-1);
  };

  if (variant === 'desktop') {
    return (
      <div ref={containerRef} className="absolute left-1/2 hidden h-11 w-[min(500px,42vw)] -translate-x-1/2 md:flex">
        <div className="relative flex h-full w-full items-center gap-3 rounded-full border border-[#cfcac2] bg-[#fcfaf6] px-5 text-[#5a6552] shadow-[0_2px_10px_rgba(35,38,35,0.03)]">
          <Search className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={1.6} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => hasQuery && setShowDropdown(true)}
            placeholder="Waar bent u op zoek naar?"
            className="w-full bg-transparent text-[13px] text-[#1a1a1a] outline-none placeholder:text-[#cfcac2]"
          />
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[#5a6552] transition-colors hover:bg-[#e7e1d6] hover:text-[#1e2b22]"
              aria-label="Wis zoekopdracht"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.2} />
            </button>
          )}
        </div>

        {showDropdown && (
          <SearchDropdown
            loading={loading}
            hasResults={hasResults}
            results={results}
            activeIndex={activeIndex}
            onResultClick={handleResultClick}
          />
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative flex h-10 w-full items-center gap-3 rounded-full border border-[#cfcac2] bg-[#fcfaf6] px-4 text-[#5a6552]">
      <Search className="h-4 w-4 flex-shrink-0" strokeWidth={1.6} />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => hasQuery && setShowDropdown(true)}
        placeholder="Waar bent u op zoek naar?"
        className="w-full bg-transparent text-[13px] outline-none placeholder:text-[#cfcac2]"
      />
      {query && (
        <button
          type="button"
          onClick={handleClear}
          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[#5a6552] transition-colors hover:bg-[#e7e1d6] hover:text-[#1e2b22]"
          aria-label="Wis zoekopdracht"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.2} />
        </button>
      )}

      {showDropdown && (
        <SearchDropdown
          loading={loading}
          hasResults={hasResults}
          results={results}
          activeIndex={activeIndex}
          onResultClick={handleResultClick}
        />
      )}
    </div>
  );
}

interface SearchDropdownProps {
  loading: boolean;
  hasResults: boolean;
  results: SearchResults;
  activeIndex: number;
  onResultClick: () => void;
}

function SearchDropdown({ loading, hasResults, results, activeIndex, onResultClick }: SearchDropdownProps) {
  let runningIndex = -1;

  const renderProduct = (product: ProductResult) => {
    runningIndex++;
    const isActive = runningIndex === activeIndex;
    return (
      <a
        key={`product-${product.id}`}
        href={product.href}
        onClick={onResultClick}
        className={`flex items-center gap-3 px-5 py-2.5 transition-colors ${isActive ? 'bg-[#f2ede4]' : 'hover:bg-[#f2ede4]'}`}
      >
        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg border border-[#e7e1d6] bg-[#e7e1d6]">
          {product.thumbnail ? (
            <img src={product.thumbnail} alt={product.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Package className="h-5 w-5 text-[#cfcac2]" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-[#1e2b22]">{product.name}</p>
          <p className="text-[11px] text-[#5a6552]">Product</p>
        </div>
        <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-[#cfcac2]" />
      </a>
    );
  };

  const renderBlog = (blog: BlogResult) => {
    runningIndex++;
    const isActive = runningIndex === activeIndex;
    return (
      <a
        key={`blog-${blog.id}`}
        href={`/blogs/${blog.slug}`}
        onClick={onResultClick}
        className={`flex items-start gap-3 px-5 py-2.5 transition-colors ${isActive ? 'bg-[#f2ede4]' : 'hover:bg-[#f2ede4]'}`}
      >
        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#e7e1d6]">
          <BookOpen className="h-4 w-4 text-[#5a6552]" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-[#1e2b22]">{blog.title}</p>
          {blog.excerpt && <p className="truncate text-[11px] text-[#5a6552]">{blog.excerpt}</p>}
        </div>
        <ArrowRight className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-[#cfcac2]" />
      </a>
    );
  };

  const renderPage = (page: PageResult) => {
    runningIndex++;
    const isActive = runningIndex === activeIndex;
    return (
      <a
        key={`page-${page.slug}`}
        href={`/klantenservice/${page.slug}`}
        onClick={onResultClick}
        className={`flex items-start gap-3 px-5 py-2.5 transition-colors ${isActive ? 'bg-[#f2ede4]' : 'hover:bg-[#f2ede4]'}`}
      >
        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#e7e1d6]">
          <FileText className="h-4 w-4 text-[#5a6552]" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-[#1e2b22]">{page.title}</p>
          {page.description && <p className="truncate text-[11px] text-[#5a6552]">{page.description}</p>}
        </div>
        <ArrowRight className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-[#cfcac2]" />
      </a>
    );
  };

  return (
    <div className="absolute left-0 right-0 top-full z-[60] mt-2 max-h-[70vh] overflow-y-auto rounded-2xl border border-[#e7e1d6] bg-[#fcfaf6] shadow-[0_12px_40px_rgba(30,43,34,0.12)]">
      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-[#5a6552]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Zoeken...
        </div>
      ) : !hasResults ? (
        <div className="py-10 text-center text-sm text-[#5a6552]">
          Geen resultaten gevonden
        </div>
      ) : (
        <div className="py-2">
          {results.products.length > 0 && (
            <div className="mb-1">
              <p className="px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#5a6552]">Producten</p>
              {results.products.map(renderProduct)}
            </div>
          )}
          {results.blogs.length > 0 && (
            <div className={`mb-1 ${results.products.length > 0 ? 'border-t border-[#e7e1d6]' : ''}`}>
              <p className="px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#5a6552]">Blogs</p>
              {results.blogs.map(renderBlog)}
            </div>
          )}
          {results.pages.length > 0 && (
            <div className={`${results.products.length > 0 || results.blogs.length > 0 ? 'border-t border-[#e7e1d6]' : ''}`}>
              <p className="px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#5a6552]">Pagina's</p>
              {results.pages.map(renderPage)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}